import React, { useState } from 'react'
import { BiChevronDown, BiChevronRight } from 'react-icons/bi'
import {
  planStepDependencyLabel,
  planStepTechnicalName,
  sortPlanSteps,
  type PlanToolStep,
} from '../utils/taskPlanDisplay'
import {
  merchantStepDetail,
  merchantStepHeadline,
  resolveStepRecipe,
} from '../utils/stepRecipes'

type Props = {
  steps: PlanToolStep[]
  /** Compact list for edit/create panels. */
  compact?: boolean
  title?: string
  /** Collapse the step list behind the title. */
  collapsible?: boolean
  defaultOpen?: boolean
  children?: React.ReactNode
}

/** Numbered execution-plan cards with merchant-friendly recipes. */
export default function TaskPlanSteps({
  steps,
  compact = false,
  title = 'How this task runs',
  collapsible = false,
  defaultOpen = true,
  children,
}: Props) {
  const [open, setOpen] = useState(defaultOpen)
  const ordered = sortPlanSteps(steps)
  if (ordered.length === 0) return null

  const heading = title ? `${title} (${ordered.length})` : null
  const list = (
      <ol className="tw-m-0 tw-list-none tw-space-y-2 tw-p-0">
        {ordered.map((step, idx) => {
          const order = step.execution_order ?? idx + 1
          const recipe = resolveStepRecipe(step.tool_name)
          const headline = merchantStepHeadline(step, recipe)
          const detail = merchantStepDetail(step, recipe)
          const dep = planStepDependencyLabel(step)
          const technical = planStepTechnicalName(step)
          const Icon = recipe.icon

          return (
            <li
              key={`${technical}-${order}`}
              className={`tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white ${
                compact ? 'tw-p-2.5' : 'tw-p-3'
              }`}
            >
              <div className="tw-flex tw-items-start tw-gap-2.5">
                <span
                  className={`tw-inline-flex tw-shrink-0 tw-items-center tw-justify-center tw-rounded-md tw-bg-blue-50 tw-font-semibold tw-text-blue-700 ${
                    compact
                      ? 'tw-h-5 tw-w-5 tw-text-[10px]'
                      : 'tw-h-6 tw-w-6 tw-text-xs'
                  }`}
                >
                  {order}
                </span>
                {Icon ? (
                  <Icon
                    className={`tw-shrink-0 tw-text-slate-400 ${
                      compact ? 'tw-h-3.5 tw-w-3.5 tw-mt-0.5' : 'tw-h-4 tw-w-4 tw-mt-1'
                    }`}
                    aria-hidden
                  />
                ) : null}
                <div className="tw-min-w-0 tw-flex-1">
                  <p
                    className={`tw-m-0 tw-font-medium tw-text-slate-900 tw-leading-snug ${
                      compact ? 'tw-text-xs' : 'tw-text-sm'
                    }`}
                  >
                    {headline}
                  </p>
                  {detail ? (
                    <p
                      className={`tw-m-0 tw-mt-0.5 tw-text-slate-600 tw-leading-snug ${
                        compact ? 'tw-text-[11px]' : 'tw-text-xs'
                      }`}
                    >
                      {detail}
                    </p>
                  ) : null}
                  {dep ? (
                    <p className="tw-m-0 tw-mt-1 tw-text-[11px] tw-font-medium tw-text-blue-600">
                      {dep}
                    </p>
                  ) : null}
                </div>
              </div>
            </li>
          )
        })}
      </ol>
  )

  if (collapsible) {
    return (
      <div className="tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white tw-overflow-hidden">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="tw-flex tw-w-full tw-items-center tw-gap-2 tw-px-3 tw-py-2.5 tw-text-left hover:tw-bg-slate-50"
        >
          {open ? (
            <BiChevronDown className="tw-h-4 tw-w-4 tw-text-slate-400" />
          ) : (
            <BiChevronRight className="tw-h-4 tw-w-4 tw-text-slate-400" />
          )}
          <span className="tw-text-xs tw-font-medium tw-text-slate-500">
            {heading || 'How this task runs'}
          </span>
        </button>
        {open && (
          <div className="tw-border-t tw-border-slate-100 tw-px-3 tw-py-3 tw-space-y-3">
            {children}
            {list}
          </div>
        )}
      </div>
    )
  }

  return (
    <div className={compact ? 'tw-space-y-2' : 'tw-space-y-3'}>
      {heading ? (
        <div
          className={`tw-font-medium tw-text-slate-500 ${
            compact ? 'tw-text-xs' : 'tw-text-[13px]'
          }`}
        >
          {heading}
        </div>
      ) : null}
      {list}
    </div>
  )
}
